<template>
    <div class="clemobil-main-container">
        <div class="header-container">
            <ClassicBreadCrumb :title="title" />
            <h1 data-cy="mobipassCreateOrEdit_maintTitle">{{ title }}</h1>
            <p data-cy="mobipassCreateOrEdit_obligatoryFields">
                {{ $t("vigik.form.obligatoryFields") }}*
            </p>
        </div>

        <div class="content-container" v-if="!loading">
            <div class="left-part-container">
                <div class="decscription-wrapper">
                    <p data-cy="mobipassCreateOrEdit_description">
                        {{ $t("clemobil.description1") }}
                    </p>
                </div>
                <div class="input-wrapper">
                    <p data-cy="mobipassCreateOrEdit_label_phoneNumber">
                        {{ $t("clemobil.form.phoneNumber") }}*
                    </p>
                    <PhoneNumber
                        class="phone-number"
                        :countries="countriesList"
                        @update:isValidPhone="isValidPhone = $event"
                        v-model="phoneNumber"
                        data-cy="mobipassCreateOrEdit_phoneNumber"
                    />
                </div>
                <div class="input-wrapper">
                    <p data-cy="mobipassCreateOrEdit_label_comment">
                        {{ $t("COMMENT") }}
                    </p>
                    <BasicInput
                        v-model="comment"
                        class="input"
                        name="comment"
                        :textarea="true"
                        maxlength="50"
                        data-cy="mobipassCreateOrEdit_comment"
                    />
                </div>
                <div class="input-wrapper" v-if="!isCreatePage">
                    <p data-cy="mobipassCreateOrEdit_label_creationDate">
                        {{ $t("clemobil.form.creationFobDate") }}
                    </p>
                    <div class="input">
                        <BasicInput
                            :placeholder="
                                formatDateWithCountry(creationFobDate)
                            "
                            data-cy="mobipassCreateOrEdit_input_createdDate"
                            disabled
                        />
                    </div>
                </div>
                <div class="input-wrapper" v-if="!isCreatePage">
                    <p data-cy="mobipassCreateOrEdit_label_updatedDate">
                        {{ $t("clemobil.form.modificationFobDate") }}
                    </p>
                    <div class="input">
                        <BasicInput
                            :placeholder="
                                formatDateWithCountry(lastChangeFobDate)
                            "
                            data-cy="mobipassCreateOrEdit_input_updatedDate"
                            disabled
                        />
                    </div>
                </div>
                <div class="input-wrapper">
                    <p data-cy="mobipassCreateOrEdit_label_deactivationDate">
                        {{ $t("clemobil.form.deactivateFobDate") }}
                    </p>
                    <div class="input">
                        <BasicDatePicker
                            class="date-picker"
                            v-model="deactivateFobDate"
                            @changeDate="
                                updateValue('deactivateFobDate', $event)
                            "
                            data-cy="mobipassCreateOrEdit_input_deactivationDate"
                        />
                    </div>
                </div>
                <div class="input-wrapper">
                    <p data-cy="mobipassCreateOrEdit_label_authorisation">
                        {{ $t("clemobil.form.authorisation") }}*
                    </p>
                    <BasicDropdown
                        v-model="authorisation"
                        name="authorisation"
                        :disabled="['0']"
                        :neededPlaceholder="true"
                        :options="authorisationOptions"
                        @update:modelValue="
                            getAccessOfAuthorisationsList(authorisation.value)
                        "
                        data-cy="mobipassCreateOrEdit_authorisations"
                    >
                        <template #subcontent="{ dataCy }">
                            <div
                                class="dropdown-actions"
                                @click="goToPhpView('autorisation')"
                                :data-cy="`${dataCy}_createAuthorisation`"
                            >
                                <i class="fas fa-plus" />
                                <p>{{ $t("clemobil.form.addAutorisation") }}</p>
                            </div>
                        </template>
                    </BasicDropdown>
                </div>
                <div class="input-wrapper" v-if="!apartementLiberal">
                    <p data-cy="mobipassCreateOrEdit_label_timeSlots">
                        {{ $t("clemobil.form.timeSlotRange") }}
                    </p>
                    <BasicDropdown
                        v-model="timeSlotRange"
                        name="timeSlotRange"
                        :neededPlaceholder="true"
                        :options="timeSlotRangeOptions"
                        data-cy="mobipassCreateOrEdit_timeSlots"
                    >
                        <template #subcontent="{ dataCy }">
                            <div
                                class="dropdown-actions"
                                @click="goToPhpView('timeSlot')"
                                :data-cy="`${dataCy}_createTimeSlots`"
                            >
                                <i class="fas fa-plus" />
                                <p>{{ $t("clemobil.form.addTimeSlot") }}</p>
                            </div>
                        </template>
                    </BasicDropdown>
                </div>
                <div class="button-wrapper">
                    <BasicButton
                        v-if="checkRight('G_KEY_MOBIL')"
                        :disabled="
                            submitLoading ||
                            !isObligatoryFields ||
                            isTheSameData
                        "
                        :loading="submitLoading"
                        @click="chooseRegisterAction"
                        data-cy="mobipassCreateOrEdit_button_createOrEdit"
                    >
                        {{ $t("button.register") }}
                    </BasicButton>
                    <BasicButton
                        v-if="isCreatePage"
                        color="blue"
                        @click="handleRedirection()"
                        data-cy="mobipassCreateOrEdit_button_cancel"
                    >
                        {{ $t("button.cancel") }}
                    </BasicButton>
                    <BasicButton
                        v-if="!isCreatePage && checkRight('G_KEY_MOBIL')"
                        color="blue"
                        @click="callDeleteMobipassAccessories"
                        :disabled="deleteLoading"
                        :loading="deleteLoading"
                        data-cy="mobipassCreateOrEdit_button_delete"
                    >
                        {{ $t("button.delete") }}
                    </BasicButton>
                    <BasicButton
                        :disabled="moveToLoading"
                        :loading="moveToLoading"
                        color="blue"
                        @click="moveTo()"
                        data-cy="mobipassCreateOrEdit_button_moveTo"
                        v-if="
                            !isCreatePage &&
                            (checkRight('G_KEY_MOVE') ||
                                checkRight('G_KEY_MOBIL')) &&
                            residenceType !== residenceTypeEnums.basic
                        "
                    >
                        {{ $t("button.moveTo") }}
                    </BasicButton>
                </div>
            </div>
            <div class="separator" v-if="displayRightPart" />
            <div class="right-part-container" v-if="displayRightPart">
                <div class="description-container">
                    <i class="fas fa-exclamation-triangle" />
                    <div class="description-wrapper">
                        <p data-cy="mobipassCreateOrEdit_warnTitle">
                            {{ $t("global.modal.warning") }}
                        </p>
                        <p data-cy="mobipassCreateOrEdit_warnDescription">
                            {{ $t("clemobil.warningDescription") }}
                        </p>
                    </div>
                </div>
                <div class="tags-wrapper">
                    <BasicChip
                        v-for="access in accessList"
                        :key="access"
                        :text="access.name"
                        :pinColor="access.pinColor"
                        :data-cy="`mobipassCreateOrEdit_tag_usedAccess_${access.id}`"
                    />
                </div>
            </div>
        </div>
        <div v-if="loading" class="loader-wrapper">
            <Loader />
        </div>
    </div>
</template>

<script>
import ClassicBreadCrumb from "@/components/entities/ClassicBreadCrumb.vue"
import BasicInput from "@/components/basic/BasicInput.vue"
import BasicDropdown from "@/components/basic/BasicDropdown.vue"
import BasicButton from "@/components/basic/BasicButton.vue"
import BasicDatePicker from "@/components/basic/BasicDatePicker.vue"
import BasicChip from "@/components/basic/BasicChip.vue"
import PhoneNumber from "@/components/entities/PhoneNumber.vue"
import Loader from "@/components/basic/Loader.vue"
import v1mixin from "@/mixins/v1.js"

import { getFobById } from "@/services/intratone/fob"
import {
    createMobipassAccessories,
    updateMobipassAccessories,
    moveMobipassAccessories,
    deleteMobipassAccessories,
} from "@/services/intratone/mobipass"
import { useRoutingToVue } from "@/composables/routingToVue"
import { getAuthorisation } from "@/services/intratone/residence"
import {
    getApartmentList,
    getApartmentSummaryById,
} from "@/services/intratone/apartment"
import { getPlages } from "@/services/intratone/plage"
import { getAccessOfAuthorisations } from "@/services/intratone/authorisation"
import { formatDateWithCountry } from "@/helpers/languageFunctions.js"
import { useToast } from "vue-toastification"

const toast = useToast()

export default {
    components: {
        ClassicBreadCrumb,
        BasicInput,
        BasicDropdown,
        BasicButton,
        BasicChip,
        BasicDatePicker,
        PhoneNumber,
        Loader,
    },
    data() {
        return {
            loading: true,
            submitLoading: false,
            deleteLoading: false,
            moveToLoading: false,
            initialValue: [],
            comment: "",
            phoneNumber: {
                code: this.$route.query.areaCode ?? "",
                number: this.$route.query.phoneNumber ?? "",
            },
            isValidPhone: false,
            creationFobDate: null,
            lastChangeFobDate: null,
            deactivateFobDate: null,
            residenceId: null,
            apartmentId: this.$route.params.apartmentId,
            apartementLiberal: null,
            authorisation: {
                name: "",
                value: "",
            },
            authorisationOptions: [
                {
                    name: `- ${this.$t("clemobil.form.chooseAuthorisation")} -`,
                    value: "0",
                },
            ],
            timeSlotRange: { name: "", value: "" },
            timeSlotRangeOptions: [
                {
                    name: `- ${this.$t("clemobil.form.chooseTimeSlotRange")} -`,
                    value: "-1",
                },
            ],

            accessList: [],
            redirectionIngnoredUrl: [
                "/v1/data/autorisation/auto",
                "/v1/data/plage",
                "/renew",
            ],
            residenceTypeEnums: {
                full: 0,
                simple: 1,
                basic: 2,
            },
        }
    },
    setup() {
        const { routerGoTo, routerGoBack } = useRoutingToVue()
        return {
            routerGoTo,
            routerGoBack,
        }
    },
    mixins: [v1mixin],
    computed: {
        isCreatePage() {
            return this.$route.params.mobipassId === "create"
        },
        title() {
            return this.isCreatePage
                ? this.$t("clemobil.create")
                : this.$t("clemobil.edit")
        },

        isObligatoryFields() {
            return (
                this.phoneNumber.number &&
                this.isValidPhone &&
                this.authorisation.value !== "0"
            )
        },

        isTheSameData() {
            return (
                this.phoneNumber.number === this.initialValue.serialgrave &&
                this.phoneNumber.code === this.initialValue.area_code &&
                this.comment === this.initialValue.comments &&
                this.deactivateFobDate === this.initialValue.dateend &&
                this.authorisation.value === this.initialValue.idautoris &&
                this.timeSlotRange.value === this.initialValue.idplage
            )
        },

        cageId() {
            return this.path.cage?.id ?? this.path.block.id
        },

        storedApartmentId() {
            const breadCrumbList =
                this.$store.getters["breadCrumb/getClassicBreadCrumb"]

            const apartment = breadCrumbList.filter(
                (item) => item.routeName === "Apartment",
            )

            return parseInt(apartment?.id) ?? null
        },

        displayRightPart() {
            return this.authorisation.value && this.authorisation.value !== "0"
        },

        sourceUrl() {
            return this.$store.getters["mobipass/getSourceUrl"]
        },

        residenceType() {
            const residenceList =
                this.$store.getters["breadCrumb/getClassicBreadCrumb"]

            const residence = residenceList.filter(
                (item) => item.routeName === "Residence",
            )

            return parseInt(residence[0]?.options?.type) ?? null
        },

        countriesList() {
            const countries =
                this.$store.getters["apiDataCache/getAllCountriesCodesList"]

            return countries.map((item) => ({
                id: item.id,
                name: "+" + item.areacode_number,
                flag: `./img/flags/${item.code_iso2}.svg`,
                value: item.code_iso2.toUpperCase(),
            }))
        },
    },
    async mounted() {
        this.loading = true

        const independantPromise = []
        await this.getApartmentInfos()

        independantPromise.push(
            this.$store.dispatch("apiDataCache/fetchAllCountriesCodeList"),
            this.getAuthorisationOptions(),
            this.getTimeRangeSlotOptions(),
        )

        await Promise.all(independantPromise)

        if (!this.isCreatePage) {
            await this.getMobipass()
            await this.getAccessOfAuthorisationsList(this.authorisation.value)
        }

        await this.retrieveLocalData()

        await this.replaceByQuery()

        this.loading = false
    },
    methods: {
        formatDateWithCountry,

        /**
         * Choose function to call if we are on create or edit page
         */
        chooseRegisterAction() {
            if (this.isCreatePage) {
                this.callCreateMobipassAccessories()
            } else {
                this.callUpdateMobipassAccessories()
            }
        },

        /**
         * Edit reactive data
         *
         * @param {string} key - Key that will be updated
         * @param {string} value - New value for selected key
         */
        updateValue(key, value) {
            this[key] = value
        },

        /**
         * Create new "MOBIPASS" fob (type: -1)
         *
         * @returns {Promise<void>}
         */
        async callCreateMobipassAccessories() {
            this.submitLoading = true

            return createMobipassAccessories({
                phone_number: this.phoneNumber.number,
                area_code: parseInt(this.phoneNumber.code),
                flat_id: parseInt(this.apartmentId),
                authorization_id: parseInt(this.authorisation.value),
                time_range_id: parseInt(this.timeSlotRange.value),
                expires_at: this.deactivateFobDate,
                comments: this.comment,
                type: -1,
            })
                .then((response) => {
                    if (response.error == 0) {
                        this.$store.dispatch("mobipass/resetMobipassStandby")
                        this.handleRedirection()
                    }
                })
                .finally(() => {
                    this.submitLoading = false
                })
        },

        /**
         * Update "MOBIPASS" fob (type: -1)
         *
         * @returns {Promise<void>}
         */
        async callUpdateMobipassAccessories() {
            this.submitLoading = true

            return updateMobipassAccessories(this.$route.params.mobipassId, {
                phone_number: this.phoneNumber.number,
                area_code: parseInt(this.phoneNumber.code),
                authorization_id: this.authorisation.value,
                time_range_id: parseInt(this.timeSlotRange.value),
                expires_at: this.deactivateFobDate,
                comments: this.comment,
            })
                .then((response) => {
                    if (response.error == 0) {
                        this.$store.dispatch("mobipass/resetMobipassStandby")
                        this.handleRedirection()
                    }
                })
                .finally(() => {
                    this.submitLoading = false
                })
        },

        /**
         * Delete fob
         *
         * @returns {Promise<void>}
         */
        async callDeleteMobipassAccessories() {
            this.openModal("validation", {
                title: "deleteTheMobipass",
                question: this.$t("clemobil.delete.question"),
                dataCy: "deleteMobipass",
                validateButton: this.$t("button.delete"),
                valid: async () => {
                    return deleteMobipassAccessories(
                        this.$route.params.mobipassId,
                    ).then((response) => {
                        if (!response) {
                            this.$store.dispatch(
                                "mobipass/resetMobipassStandby",
                            )
                            this.handleRedirection()
                        }
                    })
                },
            })
        },

        /**
         * Get MOBIPASS informations
         *
         * @returns {Promise<void>}
         */
        async getMobipass() {
            const id = this.$route.params.mobipassId

            return getFobById(id).then((response) => {
                this.initialValue = response.data
                this.phoneNumber = {
                    code: `+${response.data.area_code}`,
                    number: response.data.serialgrave,
                }
                this.creationFobDate = response.data.add_at
                this.lastChangeFobDate = response.data.mod_at
                this.deactivateFobDate = response.data.dateend
                this.comment = response.data.comments

                this.authorisation = this.authorisationOptions.find((e) => {
                    return e.value == response.data.idautoris
                })

                this.timeSlotRange =
                    this.timeSlotRangeOptions.find((e) => {
                        return e.value == response.data.idplage
                    }) ?? this.timeSlotRangeOptions[0]

                this.path = response.data.path
            })
        },

        /**
         * Get apartment entity infos
         */
        async getApartmentInfos() {
            return getApartmentSummaryById(this.apartmentId, {
                fields: "path",
            }).then((response) => {
                this.residenceId = response.data.path.residence.id
                this.apartementLiberal =
                    parseInt(response.data.appart.liberale) !== 0
            })
        },

        /**
         * Get authorisations of residence
         *
         * @returns {Promise<void>}
         */
        async getAuthorisationOptions() {
            return getAuthorisation({
                idres: this.residenceId,
                rw: 0,
                limit: 1100,
            }).then((firstResponse) => {
                // If there is more than 1 page of results, call them all
                if (firstResponse.data._pages > 1) {
                    return getAuthorisation({
                        idres: this.residenceId,
                        limit: firstResponse.data._count,
                    }).then((secondResponse) => {
                        // Add in options list
                        this.authorisationOptions.push(
                            ...secondResponse.data.list.map((item) => ({
                                name: item.nom,
                                value: item.id,
                            })),
                        )
                    })
                }

                // Add in options list
                this.authorisationOptions.push(
                    ...firstResponse.data.list.map((item) => ({
                        name: item.nom,
                        value: item.id,
                    })),
                )
            })
        },

        /**
         * Get time range of the account
         *
         * @return {Promise<void>}
         */
        async getTimeRangeSlotOptions() {
            return getPlages({ limit: 10, contxt: 2 }).then((firstResponse) => {
                // If there is more than 1 page of results, call them all
                if (firstResponse.data._pages > 1) {
                    return getPlages({
                        limit: firstResponse.data._count,
                    }).then((secondResponse) => {
                        // Add in options list
                        this.timeSlotRangeOptions.push(
                            ...secondResponse.data.list.map((item) => ({
                                name: item.nom,
                                value: item.id,
                            })),
                        )
                    })
                }

                // Add in options list
                this.timeSlotRangeOptions.push(
                    ...firstResponse.data.list.map((item) => ({
                        name: item.nom,
                        value: item.id,
                    })),
                )
            })
        },

        /**
         * Redirect to PHP view.
         *
         * @param {string} choice - Choices : 'autorisation' | 'timeSlot'
         */
        goToPhpView(choice) {
            // Save data in $store for futur use
            const saveData = {
                id: this.$route.params.mobipassId,
                serialgrave: this.phoneNumber.number,
                areacode: this.phoneNumber.code,
                datestart: this.creationFobDate,
                datechange: this.lastChangeFobDate,
                expires_at: this.deactivateFobDate,
                comments: this.comment,
                idautoris: this.authorisation.value,
                idplage: this.timeSlotRange.value,
            }

            this.$store.dispatch("mobipass/setMobipassStandby", saveData)

            switch (choice) {
                case "autorisation":
                    this.goToV1View(
                        `data/autorisation/auto/id=0&idres=${
                            this.residenceId
                        }&ret=-1&actionId=${
                            this.$route.params.mobipassId !== "create"
                                ? this.$route.params.mobipassId
                                : 0
                        }&context=10&apartmentId=${this.apartmentId}`,
                    )
                    break

                case "timeSlot":
                    this.goToV1View(
                        `data/plage/&ID=0&apartmentId=${
                            this.apartmentId
                        }&ret=-1&actionId=${
                            this.$route.params.mobipassId !== "create"
                                ? this.$route.params.mobipassId
                                : 0
                        }&context=10`,
                    )
                    break
            }
        },

        /**
         * Move MOBIPASS in other apartment
         */
        async moveTo() {
            this.moveToLoading = true

            // Add the highest element in options array. Here, the cage
            const options = [
                {
                    name: this.path?.cage
                        ? this.path.cage.name
                        : this.path.residence.name,
                    value: this.cageId,
                    type: this.path?.cage ? "CAGE" : 1,
                    selectable: false,
                    children: [],
                },
            ]

            // Retrieve all choices, here the apartments
            await getApartmentList({ idcage: this.cageId })
                .then(async (response) => {
                    options[0].children.push(
                        ...(await this.formatApartementList(
                            response.data.list,
                        )),
                    )
                })
                .finally(() => {
                    this.moveToLoading = false
                })

            // Display options
            this.openModal("move-to", {
                title: "moveTo",
                choices: options,
                dataCy: "mobipassCreateOrEdit",
                valid: (data) => {
                    return moveMobipassAccessories(
                        this.$route.params.mobipassId,
                        {
                            flat_id: data.value,
                        },
                    ).then((response) => {
                        if (response.error == 0) {
                            this.$store.dispatch(
                                "mobipass/resetMobipassStandby",
                            )

                            this.routerGoTo({
                                name: "Apartment",
                                params: {
                                    id: data.value,
                                },
                            })
                        }
                    })
                },
            })
        },

        /**
         * @typedef {Object} ApartmentRaw
         * @property {number|string} id - Apart ID
         * @property {string} nom - Apart name
         */
        /**
         * Format an apart list to display it
         *
         * @param {ApartmentRaw[]} oldData - List of aparts to be formated.
         * @returns {Array} - Formated array
         */
        formatApartementList(oldData) {
            return oldData.map((element) => ({
                name: element.nom,
                value: element.id,
                type: "APPARTEMENT",
                selectable: this.apartmentId !== element.id,
            }))
        },

        /**
         * Allow to display all centrales that use the authorisation
         *
         * @param {string} id - Id of authorisation used by MOBIPASS.
         * @returns {Promise<void>}
         */
        async getAccessOfAuthorisationsList(id) {
            if (parseInt(id)) {
                return getAccessOfAuthorisations(id).then((response) => {
                    this.accessList = response.data.list.map((e) => ({
                        name: e.nom,
                        pinColor: e.mobipass === "0" ? "orange" : null,
                        id: e.idacces,
                    }))
                })
            }
        },

        /**
         * Retrieve data saved in store if the user has previously left the view
         *
         * If we come from authorization or timeslot php view, we need to select element was created previously if exists
         */
        async retrieveLocalData() {
            const retrievedData =
                this.$store.getters["mobipass/getMobipassStandby"]

            // If data has not expired, retrieve all
            if (
                retrievedData &&
                retrievedData.id == this.$route.params.mobipassId &&
                retrievedData.timeExpiration > Date.now()
            ) {
                this.phoneNumber.number = retrievedData.serialgrave
                this.phoneNumber.code = `+${retrievedData.areacode}`
                this.creationFobDate = retrievedData.add_at
                this.lastChangeFobDate = retrievedData.mod_at
                this.deactivateFobDate = retrievedData.expires_at
                this.comment = retrievedData.comments
                this.authorisation = this.authorisationOptions.find((e) => {
                    return (
                        parseInt(e.value) === parseInt(retrievedData.idautoris)
                    )
                }) ?? { name: "", value: "" }

                this.timeSlotRange = this.timeSlotRangeOptions.find((e) => {
                    return parseInt(e.value) === parseInt(retrievedData.idplage)
                }) ?? { name: "", value: "" }
            }
        },

        /**
         * Check query and assign a value to its selection field
         */
        async replaceByQuery() {
            if (this.$route.query.authorizationId) {
                this.authorisation = this.authorisationOptions.find((e) => {
                    return (
                        Number(e.value) ===
                        Number(this.$route.query.authorizationId)
                    )
                })
                await this.getAccessOfAuthorisationsList(
                    this.authorisation.value,
                )
            }

            if (this.$route.query.timerangeId) {
                this.timeSlotRange = this.timeSlotRangeOptions.find((e) => {
                    return (
                        Number(e.value) ===
                        Number(this.$route.query.timerangeId)
                    )
                })
            }
        },

        /**
         * Manage the redirect on the original calling page
         */
        handleRedirection() {
            if (this.$route.query.residantId) {
                this.$router.push(
                    `/v1/data/fiche/rech=0&id=${this.$route.query.residantId}`,
                )
            } else if (this.sourceUrl) {
                this.$router.push(this.sourceUrl)
            } else {
                this.routerGoTo({
                    name: "Apartment",
                    params: { id: this.apartmentId },
                })
            }
        },
    },

    beforeRouteEnter: (to, from, next) => {
        next((vm) => {
            const isFromIgnoredUrl = vm.redirectionIngnoredUrl.find((url) =>
                from.fullPath.includes(url),
            )

            if (!isFromIgnoredUrl)
                vm.$store.dispatch("mobipass/setSourceUrl", from.fullPath)
        })
    },

    beforeRouteLeave(to) {
        const isToIgnoredUrl = this.redirectionIngnoredUrl.find((url) =>
            to.fullPath.includes(url),
        )

        if (!isToIgnoredUrl) {
            this.$store.dispatch("mobipass/setSourceUrl", null)
        }
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global.scss";

h1,
p {
    margin: 0;
}

.clemobil-main-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    gap: 3em;
    padding: 20px 80px 40px 80px;

    @media all and (max-width: 1024px) {
        padding: 1em;
        overflow-y: auto;
    }

    .header-container {
        display: flex;
        flex-direction: column;
        gap: 1em;

        h1 {
            font-size: $veryBig;
            font-family: $font_avenir_black;
        }

        p {
            font-size: $semiNormal;
        }
    }

    .content-container {
        width: 100%;
        // height: 100%;
        display: flex;
        overflow: auto;

        .left-part-container {
            width: 100%;
            max-width: 600px;
            height: 100%;
            display: flex;
            flex-direction: column;
            padding-right: 2em;
            overflow: auto;
            min-height: 0;

            gap: 1em;

            @media all and (max-width: 640px) {
                width: 100%;
            }

            .input-wrapper {
                display: flex;

                gap: 2em;
                width: 100%;

                @media all and (max-width: 1024px) {
                    flex-direction: column;
                    gap: 0.5em;
                }

                p {
                    min-width: 150px;
                    width: 150px;

                    @media all and (max-width: 1024px) {
                        width: 100%;
                    }
                }

                p:nth-child(2) {
                    width: 100%;
                }

                .add-button {
                    display: flex;
                    gap: 1em;
                    align-items: center;
                    cursor: pointer;
                }

                .input {
                    width: 100%;
                }
            }
        }

        .separator {
            width: 1px;
            height: 100%;
            background-color: $blue-neutral;

            @media all and (max-width: 640px) {
                display: none;
            }
        }

        .right-part-container {
            width: 50%;
            display: flex;
            flex-direction: column;
            gap: 1em;
            padding-left: 2em;

            @media all and (max-width: 640px) {
                display: none;
            }

            .description-container {
                display: flex;
                gap: 1em;

                i {
                    color: $tag-yellow;
                    padding-top: 0.2em;
                }

                .description-wrapper {
                    display: flex;
                    flex-direction: column;

                    p:nth-child(1) {
                        font-family: $font_avenir_heavy;
                        margin: 0;
                    }
                }
            }

            .tags-wrapper {
                display: flex;
                flex-wrap: wrap;
                gap: 1em;
            }
        }
    }

    .button-wrapper {
        display: flex;
        gap: 1em;
    }
}

.loader-wrapper {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>

<style lang="scss"></style>
